import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Video, FileText, Package, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Before / After case-study section for the AIgnyx landing page.
 *
 * The product data (name, hooks, storyboard scene) is a *real* beta-user
 * generation pulled from MongoDB (project "Сирене саяна", user_c477c82b31e4).
 * Never invented — every string below matches the actual DB record.
 */

// Real beta-user data — kept in Bulgarian (source of truth). Localised UI
// chrome (labels, headings) surrounds this content.
const REAL_CASE = {
  input: {
    productName: 'Сирене "саяна"',
    description: 'Много вкусно българско сирене от краве мляко',
    audience: 'Всички',
    platform: 'TikTok',
    language: 'Български',
  },
  hooks: [
    { type: 'curiosity', text: 'Кое е онова сирене, което изчезва от чинията още преди салатата? Погледни това…' },
    { type: 'problem_solution', text: 'Омръзна ли ти „сирене", което е без вкус? Смени го със „Саяна" — краве мляко, истински български вкус.' },
    { type: 'transformation', text: 'Преди: суха закуска и тъжна салата. След: една щипка „Саяна" и изведнъж всичко е по-вкусно.' },
  ],
  scene: {
    number: 1,
    title: 'Hook',
    description: '„СТОП! Ако сиренето ти не те кара да си оближеш пръстите… гледай това!"',
    camera: 'Close-up',
    duration: '3s',
  },
};

const STRINGS = {
  en: {
    kicker: 'Real beta case study',
    title: 'See AIgnyx in Action',
    subtitle: 'From a one-line product description to a shoot-ready storyboard and AI video — in under 90 seconds. Real data from a Bulgarian beta user.',
    step1Label: 'Input',
    step1Desc: 'What the user typed',
    step2Label: 'AI Generation',
    step2Desc: '5 hooks · 5-scene storyboard',
    step2HooksLabel: 'Viral hooks generated',
    step2SceneLabel: 'Storyboard scene 1 of 5',
    step3Label: 'Video-ready',
    step3Desc: 'One-click to Kling AI',
    step3Kicker: '→ Send to Kling',
    step3Caption: 'AI video in ~90 seconds',
    step3Note: 'Bridge also supports Runway and Pika. Kling marked "Best fit" for UGC.',
    fields: { product: 'Product', desc: 'Description', audience: 'Audience', platform: 'Platform', language: 'Language' },
    hookTypes: { curiosity: 'Curiosity', problem_solution: 'Problem-Solution', transformation: 'Transformation' },
  },
  bg: {
    kicker: 'Реален случай от beta',
    title: 'Как работи AIgnyx',
    subtitle: 'От еднoредово описание на продукта до готов storyboard за снимане и AI видео — за под 90 секунди. Реални данни от български beta потребител.',
    step1Label: 'Вход',
    step1Desc: 'Какво въведе потребителят',
    step2Label: 'AI генерация',
    step2Desc: '5 hook-а · Storyboard с 5 кадъра',
    step2HooksLabel: 'Генерирани вирусни hook-ове',
    step2SceneLabel: 'Storyboard кадър 1 от 5',
    step3Label: 'Готово за видео',
    step3Desc: 'Един клик към Kling AI',
    step3Kicker: '→ Изпрати към Kling',
    step3Caption: 'AI видео за ~90 секунди',
    step3Note: 'Bridge-ът поддържа още Runway и Pika. Kling е маркиран „Best fit" за UGC.',
    fields: { product: 'Продукт', desc: 'Описание', audience: 'Аудитория', platform: 'Платформа', language: 'Език' },
    hookTypes: { curiosity: 'Любопитство', problem_solution: 'Проблем-Решение', transformation: 'Трансформация' },
  },
  es: {
    kicker: 'Caso real de beta',
    title: 'AIgnyx en acción',
    subtitle: 'De una descripción de producto de una línea a un storyboard listo para rodar y un video de IA — en menos de 90 segundos. Datos reales de un usuario beta búlgaro.',
    step1Label: 'Entrada',
    step1Desc: 'Lo que escribió el usuario',
    step2Label: 'Generación IA',
    step2Desc: '5 hooks · Storyboard de 5 escenas',
    step2HooksLabel: 'Hooks virales generados',
    step2SceneLabel: 'Escena 1 de 5 del storyboard',
    step3Label: 'Listo para vídeo',
    step3Desc: 'Un clic hacia Kling AI',
    step3Kicker: '→ Enviar a Kling',
    step3Caption: 'Video IA en ~90 segundos',
    step3Note: 'El puente también admite Runway y Pika. Kling marcado "Best fit" para UGC.',
    fields: { product: 'Producto', desc: 'Descripción', audience: 'Audiencia', platform: 'Plataforma', language: 'Idioma' },
    hookTypes: { curiosity: 'Curiosidad', problem_solution: 'Problema-Solución', transformation: 'Transformación' },
  },
  de: {
    kicker: 'Echte Beta-Fallstudie',
    title: 'AIgnyx in Aktion',
    subtitle: 'Von einer einzeiligen Produktbeschreibung zu einem drehfertigen Storyboard und KI-Video — in unter 90 Sekunden. Echte Daten von einem bulgarischen Beta-Nutzer.',
    step1Label: 'Eingabe',
    step1Desc: 'Was der Nutzer eingegeben hat',
    step2Label: 'KI-Generierung',
    step2Desc: '5 Hooks · 5-Szenen-Storyboard',
    step2HooksLabel: 'Generierte virale Hooks',
    step2SceneLabel: 'Storyboard-Szene 1 von 5',
    step3Label: 'Video-ready',
    step3Desc: 'Ein Klick zu Kling AI',
    step3Kicker: '→ An Kling senden',
    step3Caption: 'KI-Video in ~90 Sekunden',
    step3Note: 'Die Brücke unterstützt auch Runway und Pika. Kling als „Best fit" für UGC markiert.',
    fields: { product: 'Produkt', desc: 'Beschreibung', audience: 'Zielgruppe', platform: 'Plattform', language: 'Sprache' },
    hookTypes: { curiosity: 'Neugier', problem_solution: 'Problem-Lösung', transformation: 'Transformation' },
  },
};

const useCaseStrings = () => {
  const { language } = useLanguage();
  return STRINGS[language] || STRINGS.en;
};

// Deep link to Kling (production users see this after generating a storyboard).
// We link to the Kling homepage here; in-app the link carries scene metadata.
const KLING_URL = 'https://klingai.com/';

function StepCard({ index, label, desc, children, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.12 }}
      className="relative flex-1 rounded-2xl border border-border/60 bg-card p-6 sm:p-7 shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center font-heading font-bold text-primary">
          {index + 1}
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg leading-tight">{label}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>
      {children}
    </motion.div>
  );
}

function Arrow() {
  return (
    <div
      className="hidden lg:flex items-center justify-center px-2 shrink-0"
      aria-hidden="true"
    >
      <div className="relative w-16">
        <div className="h-px bg-gradient-to-r from-primary/10 via-primary/50 to-primary/10" />
        <ArrowRight className="w-4 h-4 text-primary absolute -top-2 -right-1" />
      </div>
    </div>
  );
}

export default function CaseStudySection() {
  const t = useCaseStrings();

  return (
    <section
      id="case-study"
      className="py-24 sm:py-32 relative"
      data-testid="case-study-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary mb-4">
            <Sparkles className="w-3 h-3" />
            {t.kicker}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            {t.title}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* 3-step timeline */}
        <div className="flex flex-col lg:flex-row items-stretch gap-6 lg:gap-0">
          {/* --- STEP 1 : Input --- */}
          <StepCard
            index={0}
            label={t.step1Label}
            desc={t.step1Desc}
            testId="case-step-1"
          >
            <div className="flex items-center gap-2 mb-4 text-primary">
              <Package className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Product Input</span>
            </div>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground mb-0.5">{t.fields.product}</dt>
                <dd className="font-heading font-semibold text-foreground" data-testid="case-product-name">
                  {REAL_CASE.input.productName}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground mb-0.5">{t.fields.desc}</dt>
                <dd className="text-foreground">{REAL_CASE.input.description}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border/40">
                <div>
                  <dt className="text-[10px] text-muted-foreground uppercase tracking-wide">{t.fields.audience}</dt>
                  <dd className="text-xs text-foreground">{REAL_CASE.input.audience}</dd>
                </div>
                <div>
                  <dt className="text-[10px] text-muted-foreground uppercase tracking-wide">{t.fields.platform}</dt>
                  <dd className="text-xs text-foreground">{REAL_CASE.input.platform}</dd>
                </div>
                <div>
                  <dt className="text-[10px] text-muted-foreground uppercase tracking-wide">{t.fields.language}</dt>
                  <dd className="text-xs text-foreground">{REAL_CASE.input.language}</dd>
                </div>
              </div>
            </dl>
          </StepCard>

          <Arrow />

          {/* --- STEP 2 : Output --- */}
          <StepCard
            index={1}
            label={t.step2Label}
            desc={t.step2Desc}
            testId="case-step-2"
          >
            <div className="flex items-center gap-2 mb-3 text-primary">
              <FileText className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">
                {t.step2HooksLabel}
              </span>
            </div>
            <div className="space-y-2.5 mb-5">
              {REAL_CASE.hooks.map((h, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-muted/40 border border-border/40 p-2.5"
                  data-testid={`case-hook-${i}`}
                >
                  <span className="inline-block text-[10px] font-medium text-primary uppercase tracking-wide mb-1">
                    {t.hookTypes[h.type] || h.type}
                  </span>
                  <p className="text-xs text-foreground leading-relaxed">
                    {h.text}
                  </p>
                </div>
              ))}
            </div>
            {/* Storyboard preview */}
            <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-medium text-primary uppercase tracking-wide">
                  {t.step2SceneLabel}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {REAL_CASE.scene.camera} · {REAL_CASE.scene.duration}
                </span>
              </div>
              <p className="text-xs font-semibold mb-1">
                #{REAL_CASE.scene.number} — {REAL_CASE.scene.title}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed italic">
                {REAL_CASE.scene.description}
              </p>
            </div>
          </StepCard>

          <Arrow />

          {/* --- STEP 3 : Kling video --- */}
          <StepCard
            index={2}
            label={t.step3Label}
            desc={t.step3Desc}
            testId="case-step-3"
          >
            {/* Phone-frame video mockup */}
            <a
              href={KLING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block relative mx-auto rounded-xl overflow-hidden group"
              style={{ aspectRatio: '9 / 16', maxWidth: '180px' }}
              data-testid="case-kling-link"
            >
              {/* Storyboard scene rendered as video-still */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(135deg, #5A50E5 0%, #7B6FFF 45%, #3F35C0 100%)',
                }}
              />
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
              />
              {/* Play indicator */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Video className="w-5 h-5 text-primary ml-0.5" />
                </div>
              </div>
              {/* Kling badge */}
              <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                <span className="text-[9px] font-semibold text-white bg-black/50 backdrop-blur px-2 py-0.5 rounded-full">
                  Kling AI
                </span>
                <span className="text-[9px] font-medium text-white/90 bg-emerald-500/80 px-1.5 py-0.5 rounded-full">
                  Best fit
                </span>
              </div>
              {/* Scene caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3">
                <p className="text-[10px] text-white/95 font-medium leading-tight">
                  {REAL_CASE.scene.description}
                </p>
              </div>
            </a>

            <a
              href={KLING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:underline"
              data-testid="case-kling-cta"
            >
              {t.step3Kicker}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {t.step3Caption}
            </p>
            <p className="mt-3 pt-3 border-t border-border/40 text-[10px] text-muted-foreground leading-relaxed">
              {t.step3Note}
            </p>
          </StepCard>
        </div>
      </div>
    </section>
  );
}
